/**
 * Technician detail drawer — view + activate/deactivate.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import * as api from '../../modules/api';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField } from '../components/DetailDrawerContent';
import { getCurrentRole } from '../utils/accessControl';

export default function ServiceTechnicianDetailDrawer({ technician, open, onClose, onRefresh }) {
  const { t } = useTranslation();
  const role = getCurrentRole();
  const canEdit = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!technician) return null;

  const active = technician.active !== false;

  const toggleActive = async () => {
    if (!canEdit || !technician.id) return;
    setBusy(true);
    setError(null);
    try {
      await api.patchServiceTechnician(technician.id, { active: !active });
      onRefresh?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t('service.errorSave'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={technician.name || technician.email || t('service.techniciansTitle')}
    >
      <Box sx={{ p: 2 }}>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <Section title={t('service.techniciansTitle')}>
          <CopyableField label={t('service.techEmail')} value={technician.email} />
          <Field label={t('service.techFullName')} value={technician.name || '—'} />
          <Field label={t('service.techPhone')} value={technician.phone || '—'} />
          <Field label={t('service.partner')} value={technician.parentAdminEmail || '—'} />
          <Field
            label={t('service.active')}
            value={(
              <Chip
                size="small"
                label={active ? t('service.activeYes') : t('service.activeNo')}
                color={active ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          />
        </Section>
        {canEdit && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color={active ? 'warning' : 'success'}
              disabled={busy}
              onClick={toggleActive}
            >
              {active ? t('service.deactivateTech') : t('service.activateTech')}
            </Button>
          </Box>
        )}
      </Box>
    </DetailDrawer>
  );
}
